import React, { FC, Fragment, useEffect, useState } from 'react';
import { Box, Button, Divider, Drawer, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import HelpSearch from './help-center/help-search.component';
import SupportTicket from './support-ticket/support-ticket.component';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import HelpArticleView from './help-center/help-article-view.component';
import { HelpArticle } from 'models/zenddesk';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { assistantQuestionVar } from 'apollo/reactive-variables/help-drawer';
import { contextHelpTemplate } from 'apollo/templates/zendesk';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useLocation, useNavigate } from 'react-router-dom';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HistoryIcon from '@mui/icons-material/History';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { highlightUiElement } from 'utils/highlight-ui-element';
import { openSupportShareWindow } from 'support-share/open-support-share-window';
import AssistantChat from './assistant/assistant-chat.component';
import AssistantHistory from './assistant/assistant-history.component';
import AssistantPastConversation from './assistant/assistant-past-conversation.component';
import { AssistantChatMessage } from 'models/assistant';

/** In fullscreen the content keeps a readable width, centred on the page. */
const CONTENT_MAX_WIDTH = 1000;
// The chat centres itself, and with an article open it uses the whole page width.
const ASSISTANT_MAX_WIDTH = 'none';
const centredContent = { width: '100%', maxWidth: CONTENT_MAX_WIDTH, mx: 'auto', boxSizing: 'border-box' } as const;

type HelpDrawerView = 'SUPPORT_TICKET' | 'HELP_SEARCH' | 'ARTICLE' | 'ASSISTANT' | 'HISTORY' | 'PAST_CONVERSATION';
const HOME_VIEW: HelpDrawerView = 'ASSISTANT';

type HelpDrawerProps = {
    helpVisible: boolean;
    onClose: () => void;
};
const HelpDrawer: FC<HelpDrawerProps> = ({ helpVisible, onClose }: HelpDrawerProps) => {
    const { t: translate } = useTranslation(['common', 'help-drawer']);
    const { showBoundary } = useDenthubErrorBoundary();
    // DD-3780: the drawer opens on the help assistant.
    const [uiState, setUiState] = useState<HelpDrawerView>(HOME_VIEW);
    // Where Back goes: the view the user came from.
    const [previousUiState, setPreviousUiState] = useState<HelpDrawerView>();
    // DD-3780: kept here so the conversation survives opening an article and coming back.
    const [assistantMessages, setAssistantMessages] = useState<AssistantChatMessage[]>([]);
    // The stored conversation the chat adds to, and the past one open in the history.
    const [conversationId, setConversationId] = useState<string>();
    const [pastConversationId, setPastConversationId] = useState<string>();
    const [supportTicketDraft, setSupportTicketDraft] = useState<string>();
    // The full width of the page, not the browser's full screen mode.
    const [isFullWidth, setIsFullWidth] = useState(false);
    const drawerWidth = isFullWidth ? '100%' : '35%';
    const [currentArticleId, setCurrentArticleId] = useState<string>();
    const [contextArticles, setContextArticles] = useState<HelpArticle[]>([]);
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();

    // A question handed over (e.g. from the error offer) always opens the chat.
    const pendingQuestion = useReactiveVar(assistantQuestionVar);
    useEffect(() => {
        if (helpVisible && pendingQuestion) {
            setUiState('ASSISTANT');
        }
    }, [helpVisible, pendingQuestion]);

    const showView = (view: HelpDrawerView) => {
        setPreviousUiState(uiState);
        setUiState(view);
    };
    // Both show the page, which a fullscreen drawer would cover. Only a click on a UI name
    // highlights anything; opening a page does not.
    const openPage = (path: string) => {
        setIsFullWidth(false);
        navigate(path);
    };
    const showUiElement = (name: string) => {
        setIsFullWidth(false);
        highlightUiElement([name], theme.palette.secondary.main);
    };

    const currentUser = currentUserVar();
    const currentOrg = currentOrgVar();

    const [contextHelp] = useLazyQuery(contextHelpTemplate, {
        onCompleted({ contextHelp }) {
            setContextArticles(contextHelp);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (helpVisible) {
            const tags = extractContextTags();
            if (tags.length > 0) {
                contextHelp({ variables: { scopeTags: tags, urlTag: getUrlAsTag() } });
            }
        }
    }, [pathname, helpVisible]);

    const extractContextTags = () => {
        const tags = [];
        if (permissions.isLabTechnician(currentUser.type)) {
            tags.push('technician-user');
        } else if (permissions.isDentist(currentUser.type)) {
            tags.push('dentist-user');
            const orderType = customerSettings.getCustomerSetting(
                currentOrg.customerSettings || [],
                CONSTANTS.SETTINGS_NAMES.ORDER_VERSION,
            );
            tags.push(orderType.toLowerCase());
        }
        if (currentUser.role.includes('ADMIN')) {
            tags.push('admin-user');
        } else {
            tags.push('normal-user');
        }
        return tags;
    };

    const getUrlAsTag = () => {
        const isMongoId = (str: string) => /^[a-f\d]{24}$/i.test(str);
        const segments = pathname.split('/').filter(Boolean);

        return segments.filter((segment) => !isMongoId(segment)).join('-');
    };

    const renderUiState = () => {
        switch (uiState) {
            case 'SUPPORT_TICKET':
                return <SupportTicket initialMessage={supportTicketDraft} onCancel={handleBackClick} />;
            case 'ASSISTANT':
                return (
                    <AssistantChat
                        messages={assistantMessages}
                        onMessagesChange={setAssistantMessages}
                        conversationId={conversationId}
                        onConversationIdChange={setConversationId}
                        isWide={isFullWidth}
                        onNavigate={openPage}
                        onShowUiElement={showUiElement}
                    />
                );
            case 'HISTORY':
                return (
                    <AssistantHistory
                        onOpenConversation={(id) => {
                            setPastConversationId(id);
                            showView('PAST_CONVERSATION');
                        }}
                    />
                );
            case 'PAST_CONVERSATION':
                return (
                    <AssistantPastConversation
                        conversationId={pastConversationId || ''}
                        onArticleClicked={handleArticleClick}
                        onNavigate={openPage}
                        onShowUiElement={showUiElement}
                    />
                );
            case 'HELP_SEARCH':
                return <HelpSearch onArticleClicked={handleArticleClick} contextArticles={contextArticles} />;
            case 'ARTICLE':
                return (
                    <HelpArticleView
                        articleId={currentArticleId || ''}
                        onLinkClick={(articleId) => setCurrentArticleId(articleId)}
                    />
                );
        }
    };

    const handleArticleClick = (articleId: string) => {
        setCurrentArticleId(articleId);
        showView('ARTICLE');
    };

    const handleCloseHelpDrawer = () => {
        setUiState(HOME_VIEW);
        setPreviousUiState(undefined);
        setSupportTicketDraft(undefined);
        setIsFullWidth(false);
        setCurrentArticleId(undefined);
        onClose();
    };

    const handleBackClick = () => {
        setCurrentArticleId(undefined);
        setSupportTicketDraft(undefined);
        const target = previousUiState && previousUiState !== uiState ? previousUiState : HOME_VIEW;
        setPreviousUiState(undefined);
        setUiState(target === 'ARTICLE' ? HOME_VIEW : target);
    };

    return (
        <Fragment>
            <Drawer
                sx={{
                    width: helpVisible ? drawerWidth : 0,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        // Header, content, footer: the content takes exactly the space left.
                        display: 'flex',
                        flexDirection: 'column',
                    },
                    color: 'black',
                }}
                variant="persistent"
                // DD-3780: the assistant's "show me" never points at the drawer's own buttons.
                PaperProps={{ 'data-help-drawer': true } as Record<string, unknown>}
                anchor="right"
                open={helpVisible}
            >
                <Box px={2} mb={1} sx={{ marginTop: '75px' }} display="flex" alignItems="center">
                    <Box display="flex" gap={1} flex="1 1 0" minWidth={0}>
                        {uiState === 'ASSISTANT' ? (
                            <Fragment>
                                <Button
                                    variant="text"
                                    startIcon={<MenuBookIcon />}
                                    onClick={() => showView('HELP_SEARCH')}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {translate('help-drawer:HELP_ARTICLES')}
                                </Button>
                                <Button
                                    variant="text"
                                    startIcon={<HistoryIcon />}
                                    onClick={() => showView('HISTORY')}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {translate('help-drawer:ASSISTANT_HISTORY')}
                                </Button>
                            </Fragment>
                        ) : undefined}
                        {uiState !== HOME_VIEW ? (
                            <Button variant="text" startIcon={<ArrowBackIosIcon />} onClick={() => handleBackClick()}>
                                {translate('common:BACK')}
                            </Button>
                        ) : undefined}
                    </Box>
                    <Box flex="0 0 auto" px={1}>
                        <Typography variant="h3">{translate('help-drawer:HELP_AND_SUPPORT')}</Typography>
                    </Box>
                    <Box display="flex" flex="1 1 0" minWidth={0} justifyContent="flex-end" gap={1}>
                        <Button
                            variant="text"
                            startIcon={isFullWidth ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                            onClick={() => setIsFullWidth(!isFullWidth)}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {translate(isFullWidth ? 'help-drawer:EXIT_FULLSCREEN' : 'help-drawer:FULLSCREEN')}
                        </Button>
                        <Button variant="text" startIcon={<CloseIcon />} onClick={handleCloseHelpDrawer}>
                            {translate('common:CLOSE')}
                        </Button>
                    </Box>
                </Box>
                <Box>
                    <Divider />
                </Box>
                <Box
                    pt={1}
                    mb={1}
                    px={2}
                    sx={{
                        ...centredContent,
                        maxWidth: uiState === 'ASSISTANT' ? ASSISTANT_MAX_WIDTH : CONTENT_MAX_WIDTH,
                        overflowY: 'auto',
                        flex: '1 1 auto',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        '& > *': { flexShrink: 0 },
                        // The chat fills the space and scrolls inside itself.
                        ...(uiState === 'ASSISTANT' ? { '& > *': { flex: '1 1 auto', minHeight: 0 } } : {}),
                    }}
                >
                    {renderUiState()}
                </Box>
                {uiState === 'HELP_SEARCH' ? (
                    <Fragment>
                        <Box mb={1.5}>
                            <Divider />
                        </Box>

                        <Stack px={2} pb={2} spacing={1} sx={centredContent}>
                            <Button fullWidth variant="contained" onClick={() => showView('SUPPORT_TICKET')}>
                                {translate('help-drawer:CREATE_SUPPORT_TICKET')}
                            </Button>
                            <Button fullWidth variant="text" onClick={openSupportShareWindow}>
                                {translate('help-drawer:LET_SUPPORT_SEE_MY_SCREEN')}
                            </Button>
                        </Stack>
                    </Fragment>
                ) : undefined}
            </Drawer>
        </Fragment>
    );
};

export default HelpDrawer;
